import { useMemo } from "react";
import { TimeRange } from "../components/routes/home/usage/TimeRangeSelect";

// Build intervals using time range
export const useTimeIntervals = (timeRange: TimeRange) => {
  return useMemo(() => {
    const start = timeRange.start ? timeRange.start : new Date();
    const end = timeRange.end ? timeRange.end : new Date();

    const intervals: { name: string; start: number; end: number }[] = [];

    // Floor/ceil start/end dates to specific hours
    const current = new Date(new Date(start).setMinutes(0));
    const last = new Date(new Date(end).setMinutes(0));

    // Automatically determine frequency from start/end
    const frequency = (() => {
      const range = end.getTime() - start.getTime();

      const oneHour = 60 * 60 * 1000;
      const oneDay = 24 * oneHour;
      const oneMonth = 30 * oneDay;

      if (range <= oneDay * 1.5) {
        return "hourly";
      } else if (range <= oneMonth * 1.5) {
        return "daily";
      } else if (range <= oneMonth * 3) {
        return "weekly";
      } else {
        return "monthly";
      }
    })();

    // Generate all time intervals between
    while (current <= last) {
      switch (frequency) {
        case "hourly": {
          const intervalStart = current.getTime();
          const name = current.toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            hour12: true,
          });
          current.setHours(current.getHours() + 1);
          const intervalEnd = current.getTime();
          intervals.push({
            name: name,
            start: intervalStart,
            end: intervalEnd,
          });
          break;
        }
        case "daily": {
          const name = current.toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          const startOfDay = new Date(current);
          startOfDay.setHours(0, 0, 0, 0);
          const endOfDay = new Date(current);
          endOfDay.setHours(23, 59, 59, 999);
          intervals.push({
            name: name,
            start: startOfDay.getTime(),
            end: endOfDay.getTime(),
          });
          current.setDate(current.getDate() + 1);
          break;
        }
        case "weekly": {
          // Get start of the week (most recent Sunday)
          const startOfWeek = new Date(current);
          const day = current.getDay();
          // Adjust to the most recent Sunday
          startOfWeek.setDate(current.getDate() - day);
          startOfWeek.setHours(0, 0, 0, 0);

          // Get end of the week (next Saturday)
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          endOfWeek.setHours(23, 59, 59, 999);

          const name = `Week of ${startOfWeek.toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}`;

          intervals.push({
            name,
            start: startOfWeek.getTime(),
            end: endOfWeek.getTime(),
          });

          // Move to the next week
          current.setDate(startOfWeek.getDate() + 7);
          break;
        }
        case "monthly": {
          const name = current.toLocaleString("en-US", {
            year: "numeric",
            month: "long",
          });
          const startOfMonth = new Date(current);
          startOfMonth.setDate(1);
          startOfMonth.setHours(0, 0, 0, 0);
          const endOfMonth = new Date(current);
          endOfMonth.setMonth(endOfMonth.getMonth() + 1);
          endOfMonth.setDate(0);
          endOfMonth.setHours(23, 59, 59, 999);
          intervals.push({
            name: name,
            start: startOfMonth.getTime(),
            end: endOfMonth.getTime(),
          });
          current.setMonth(current.getMonth() + 1);
          break;
        }
        default:
          break;
      }
    }

    return intervals;
  }, [timeRange]);
};
